import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { MdCheckCircle, MdCancel, MdExpandLess, MdExpandMore } from 'react-icons/md';
import { toast } from 'react-toastify';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { formatDisplayDate, formatDisplayDateTime } from '@/lib/formatters';
import { urls } from '@/urls';
import { COLORS } from '@/theme/colors';
import {
  SyncDateRange,
  SyncDateRangePicker,
  isValidSyncDateRange,
  syncDateRangeFromLog,
  toSyncDatePayload,
} from './sync-date-range';

type SyncLogOverallStatus = 'success' | 'failed' | 'running' | 'pending';

type SyncLogStepStatus = 'running' | 'success' | 'failed' | null;

interface SyncLogStep {
  step: string;
  label: string;
  status: SyncLogStepStatus;
  finishedAt: string | null;
  error: string | null;
}

interface SyncLogSummary {
  id: number;
  syncRunId: number | null;
  subscriptionId: string | null;
  subscriptionName: string | null;
  cloudAccountId: number | null;
  cloudAccountName: string | null;
  trigger: 'manual' | 'auto' | null;
  userEmail: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  startedAt: string;
  overallStatus: SyncLogOverallStatus;
  failedStep: string | null;
  subscriptionCount?: number;
  failedSubscriptionCount?: number;
}

interface SyncLogDetail extends SyncLogSummary {
  steps: SyncLogStep[];
  subscriptions?: SyncLogDetail[];
}

const STATUS_CHIP: Record<
  SyncLogOverallStatus,
  { label: string; color: 'success' | 'error' | 'info' | 'default' }
> = {
  success: { label: 'Success', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  running: { label: 'Running', color: 'info' },
  pending: { label: 'Pending', color: 'default' },
};

function StatusIcon({ status }: { status: SyncLogOverallStatus }) {
  if (status === 'success') {
    return <MdCheckCircle size={22} color={COLORS.mutedGreen} />;
  }
  if (status === 'failed') {
    return <MdCancel size={22} color={COLORS.danger} />;
  }
  if (status === 'running') {
    return <CircularProgress size={18} />;
  }
  return null;
}

function StepStatusIcon({ status }: { status: SyncLogStepStatus }) {
  if (status === 'success') {
    return <MdCheckCircle size={18} color={COLORS.mutedGreen} />;
  }
  if (status === 'failed') {
    return <MdCancel size={18} color={COLORS.danger} />;
  }
  if (status === 'running') {
    return <CircularProgress size={16} />;
  }
  return <Typography variant="body2" color="text.secondary">—</Typography>;
}

function formatDateTime(value: string | null, withTime = true) {
  if (!value) return '—';
  const date = new Date(value);
  return withTime ? formatDisplayDateTime(date) : formatDisplayDate(date);
}

function formatSubscription(summary: SyncLogSummary) {
  const count = summary.subscriptionCount ?? 1;
  if (count > 1) {
    return `${count} subscriptions`;
  }
  return summary.subscriptionName ?? summary.subscriptionId ?? '—';
}

function SyncStepsTable({ steps }: { steps: SyncLogStep[] }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell width={40} />
          <TableCell>Step</TableCell>
          <TableCell>Finished</TableCell>
          <TableCell>Error</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {steps.map((step) => (
          <TableRow key={step.step}>
            <TableCell>
              <StepStatusIcon status={step.status} />
            </TableCell>
            <TableCell>{step.label}</TableCell>
            <TableCell>{formatDateTime(step.finishedAt)}</TableCell>
            <TableCell>
              {step.error ? (
                <Typography variant="body2" color="error">
                  {step.error}
                </Typography>
              ) : (
                '—'
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SubscriptionRow({
  subscription,
  dateRange,
  onResync,
  resyncing,
}: {
  subscription: SyncLogDetail;
  dateRange: SyncDateRange;
  onResync: (id: number, dateRange: SyncDateRange) => void;
  resyncing: (id: number) => boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const subscriptionChip = STATUS_CHIP[subscription.overallStatus];
  const hasSteps = subscription.steps && subscription.steps.length > 0;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: hasSteps ? 'pointer' : 'default' }}
        onClick={() => hasSteps && setExpanded((current) => !current)}
        data-testid={`sync-log-subscription-row-${subscription.id}`}>
        <TableCell padding="checkbox">
          {hasSteps && (
            <IconButton
              size="small"
              aria-label={expanded ? 'Collapse' : 'Expand'}>
              {expanded ? <MdExpandLess /> : <MdExpandMore />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <StatusIcon status={subscription.overallStatus} />
        </TableCell>
        <TableCell>
          {subscription.subscriptionName ?? subscription.subscriptionId ?? '—'}
        </TableCell>
        <TableCell>{subscription.failedStep ?? '—'}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={subscriptionChip.label}
            color={subscriptionChip.color}
          />
        </TableCell>
        <TableCell align="right">
          {subscription.overallStatus === 'failed' &&
          subscription.subscriptionId ? (
            <Button
              variant="outlined"
              size="small"
              disabled={resyncing(subscription.id)}
              onClick={(event) => {
                event.stopPropagation();
                if (!isValidSyncDateRange(dateRange)) {
                  toast.error('Date from must be before date to');
                  return;
                }
                onResync(subscription.id, dateRange);
              }}
              data-testid={`sync-log-resync-${subscription.id}`}>
              {resyncing(subscription.id)
                ? 'Scheduling…'
                : 'Resync subscription'}
            </Button>
          ) : (
            '—'
          )}
        </TableCell>
      </TableRow>
      {hasSteps && (
        <TableRow>
          <TableCell
            colSpan={6}
            sx={{ py: 0, borderBottom: expanded ? undefined : 0 }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2, pl: 4, pr: 2 }}>
                <SyncStepsTable steps={subscription.steps} />
                {(subscription.dateFrom || subscription.dateTo) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Date range: {formatDateTime(subscription.dateFrom, false)} —{' '}
                      {formatDateTime(subscription.dateTo, false)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function SyncHistoryRow({
  summary,
  expanded,
  onToggle,
  onResync,
  onResyncAllFailed,
  resyncing,
  resyncingRun,
}: {
  summary: SyncLogSummary;
  expanded: boolean;
  onToggle: () => void;
  onResync: (id: number, dateRange: SyncDateRange) => void;
  onResyncAllFailed: (ids: number[], dateRange: SyncDateRange) => void;
  resyncing: (id: number) => boolean;
  resyncingRun: (runId: number) => boolean;
}) {
  const [detail, setDetail] = useState<SyncLogDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [resyncDateRange, setResyncDateRange] = useState<SyncDateRange>({
    dateFrom: null,
    dateTo: null,
  });

  useEffect(() => {
    if (!expanded) return;

    let cancelled = false;

    const loadDetail = (showLoader: boolean) => {
      if (showLoader) setLoadingDetail(true);
      fetchWrapper
        .get(urls.DevOps.syncLogById(summary.id))
        .then((data: SyncLogDetail) => {
          if (cancelled) return;
          setDetail(data);
          setResyncDateRange((current) =>
            current.dateFrom || current.dateTo
              ? current
              : syncDateRangeFromLog(data.dateFrom, data.dateTo),
          );
        })
        .catch(() => {
          if (!cancelled) toast.error('Failed to load sync details');
        })
        .finally(() => {
          if (!cancelled && showLoader) setLoadingDetail(false);
        });
    };

    loadDetail(true);

    const intervalId = window.setInterval(() => {
      if (summary.overallStatus === 'running') {
        loadDetail(false);
      }
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [expanded, summary.id, summary.overallStatus]);

  const chip = STATUS_CHIP[summary.overallStatus];
  const failedSubscriptionIds =
    detail?.subscriptions
      ?.filter(
        (subscription) =>
          subscription.overallStatus === 'failed' && subscription.subscriptionId,
      )
      .map((subscription) => subscription.id) ?? [];
  const canResync =
    (detail?.overallStatus === 'failed' && !!detail.subscriptionId) ||
    failedSubscriptionIds.length > 0;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer' }}
        onClick={onToggle}
        data-testid={`sync-log-row-${summary.id}`}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={expanded ? 'Collapse' : 'Expand'}>
            {expanded ? <MdExpandLess /> : <MdExpandMore />}
          </IconButton>
        </TableCell>
        <TableCell>
          <StatusIcon status={summary.overallStatus} />
        </TableCell>
        <TableCell>{formatDateTime(summary.startedAt)}</TableCell>
        <TableCell>{formatSubscription(summary)}</TableCell>
        <TableCell>{summary.cloudAccountName ?? '—'}</TableCell>
        <TableCell>{summary.trigger ?? '—'}</TableCell>
        <TableCell>
          <Chip size="small" label={chip.label} color={chip.color} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded ? undefined : 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, pl: 6, pr: 2 }}>
              {loadingDetail ? (
                <CircularProgress size={24} />
              ) : detail ? (
                <>
                  {detail.subscriptions && detail.subscriptions.length > 1 ? (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Subscriptions
                      </Typography>
                      {canResync && (
                        <SyncDateRangePicker
                          value={resyncDateRange}
                          onChange={setResyncDateRange}
                          compact
                        />
                      )}
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell width={40} />
                            <TableCell width={40} />
                            <TableCell>Subscription</TableCell>
                            <TableCell>Failed step</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detail.subscriptions.map((subscription) => (
                            <SubscriptionRow
                              key={subscription.id}
                              subscription={subscription}
                              dateRange={resyncDateRange}
                              onResync={onResync}
                              resyncing={resyncing}
                            />
                          ))}
                        </TableBody>
                      </Table>
                      {failedSubscriptionIds.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={resyncingRun(summary.id)}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (!isValidSyncDateRange(resyncDateRange)) {
                                toast.error('Date from must be before date to');
                                return;
                              }
                              onResyncAllFailed(
                                failedSubscriptionIds,
                                resyncDateRange,
                              );
                            }}
                            data-testid={`sync-log-resync-all-failed-${summary.id}`}>
                            {resyncingRun(summary.id)
                              ? 'Scheduling…'
                              : `Resync all failed (${failedSubscriptionIds.length})`}
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Sync steps
                      </Typography>
                      <SyncStepsTable steps={detail.steps} />
                    </>
                  )}
                  {(detail.dateFrom || detail.dateTo || detail.userEmail) && (
                    <Box sx={{ mt: 2 }}>
                      {detail.userEmail && (
                        <Typography variant="body2" color="text.secondary">
                          Triggered by: {detail.userEmail}
                        </Typography>
                      )}
                      {(detail.dateFrom || detail.dateTo) && (
                        <Typography variant="body2" color="text.secondary">
                          Date range: {formatDateTime(detail.dateFrom, false)} —{' '}
                          {formatDateTime(detail.dateTo, false)}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {detail.overallStatus === 'failed' && detail.subscriptionId && (
                    <Box sx={{ mt: 2 }}>
                      <SyncDateRangePicker
                        value={resyncDateRange}
                        onChange={setResyncDateRange}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={resyncing(detail.id)}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!isValidSyncDateRange(resyncDateRange)) {
                            toast.error('Date from must be before date to');
                            return;
                          }
                          onResync(detail.id, resyncDateRange);
                        }}
                        data-testid={`sync-log-resync-${summary.id}`}>
                        {resyncing(detail.id) ? 'Scheduling…' : 'Resync subscription'}
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Alert severity="warning">Could not load sync details.</Alert>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function SyncHistory() {
  const [logs, setLogs] = useState<SyncLogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [resyncingIds, setResyncingIds] = useState<Set<number>>(() => new Set());
  const [resyncingRunId, setResyncingRunId] = useState<number | null>(null);
  const [failedOnly, setFailedOnly] = useState(false);

  const load = useCallback(async () => {
    const data = await fetchWrapper.get(
      failedOnly ? `${urls.DevOps.syncLogs}?status=failed` : urls.DevOps.syncLogs,
    );
    setLogs(data);
  }, [failedOnly]);

  useEffect(() => {
    load()
      .catch(() => toast.error('Failed to load sync history'))
      .finally(() => setLoading(false));
  }, [load]);

  const hasRunningLogs = logs.some((log) => log.overallStatus === 'running');

  useEffect(() => {
    if (!hasRunningLogs) return;
    const intervalId = window.setInterval(() => {
      load().catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(intervalId);
  }, [hasRunningLogs, load]);

  const scheduleRefresh = () => {
    setTimeout(() => {
      load().catch(() => undefined);
    }, 2000);
  };

  const handleResync = async (id: number, dateRange: SyncDateRange) => {
    if (!isValidSyncDateRange(dateRange)) {
      toast.error('Date from must be before date to');
      return;
    }
    setResyncingIds((current) => new Set(current).add(id));
    try {
      const res = await fetchWrapper.post(
        urls.DevOps.syncLogResync(id),
        toSyncDatePayload(dateRange),
      );
      toast.success(res.message ?? 'Resync scheduled');
      scheduleRefresh();
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to schedule resync');
    } finally {
      setResyncingIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  };

  const handleResyncAllFailed = async (
    runId: number,
    ids: number[],
    dateRange: SyncDateRange,
  ) => {
    if (ids.length === 0) return;
    if (!isValidSyncDateRange(dateRange)) {
      toast.error('Date from must be before date to');
      return;
    }
    const payload = { ...toSyncDatePayload(dateRange), ids };
    setResyncingRunId(runId);
    setResyncingIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => next.add(id));
      return next;
    });
    try {
      const res = await fetchWrapper.post(
        urls.DevOps.syncLogResyncFailed,
        payload,
      );
      toast.success(res.message ?? 'Resync scheduled');
      scheduleRefresh();
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to schedule resync');
    } finally {
      setResyncingRunId(null);
      setResyncingIds((current) => {
        const next = new Set(current);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Sync history</Typography>
        <Typography variant="body2" color="text.secondary">
          Recent cloud subscription synchronizations. Click a row to see each
          step and when it finished. Failed runs can be retried from the
          expanded view.
        </Typography>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Switch
              checked={failedOnly}
              onChange={(event) => {
                setExpandedId(null);
                setFailedOnly(event.target.checked);
              }}
            />
          }
          label="Failed only"
        />
      </Grid>
      <Grid item xs={12}>
        {logs.length === 0 ? (
          <Alert severity="info">
            {failedOnly
              ? 'No failed sync runs recorded yet.'
              : 'No sync runs recorded yet.'}
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={48} />
                <TableCell width={40} />
                <TableCell>Started</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Cloud account</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <SyncHistoryRow
                  key={log.id}
                  summary={log}
                  expanded={expandedId === log.id}
                  onToggle={() =>
                    setExpandedId((current) => (current === log.id ? null : log.id))
                  }
                  onResync={handleResync}
                  onResyncAllFailed={(ids, dateRange) =>
                    handleResyncAllFailed(log.id, ids, dateRange)
                  }
                  resyncing={(id) => resyncingIds.has(id)}
                  resyncingRun={(runId) => resyncingRunId === runId}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Grid>
      <Grid item xs={12}>
        <Button variant="text" onClick={() => load().catch(() => undefined)}>
          Refresh
        </Button>
      </Grid>
    </Grid>
  );
}

export default SyncHistory;
